import React from 'react';
import { motion } from 'framer-motion';
import { Database, Workflow, GraduationCap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const services = [
  {
    icon: Database,
    title: 'Data Labeling & Annotation',
    description: 'We label, categorize, and structure your raw data into high-quality training sets that AI models actually learn from.',
    image: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b2907c6ea2c641fd46240c/947322834_generated_5685dc6a.png',
    color: 'blue',
  },
  {
    icon: Workflow,
    title: 'AI Workflow Integration',
    description: 'We embed AI tools directly into your existing processes — no disruption, just smarter operations from day one.',
    image: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b2907c6ea2c641fd46240c/8c1521adb_generated_99cb02ec.png',
    color: 'amber',
  },
  {
    icon: GraduationCap,
    title: 'Specialized Domain Training',
    description: 'From legal documents to medical records, we train models on your industry-specific data with expert-level precision.',
    image: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b2907c6ea2c641fd46240c/61d0dfdd2_generated_18809211.png',
    color: 'emerald',
  },
];

const colorClasses = {
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', glow: 'group-hover:shadow-blue-500/10' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', glow: 'group-hover:shadow-amber-500/10' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'group-hover:shadow-emerald-500/10' },
};

export default function ServicesPreview() {
  return (
    <section className="bg-slate-950 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-blue-400 text-sm font-semibold tracking-widest uppercase">What We Do</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white tracking-tight">
            End-to-end AI enablement
          </h2>
          <p className="mt-4 text-slate-400 text-lg max-w-2xl mx-auto">
            From raw data to running AI — we handle the entire journey so you can focus on your business.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, i) => {
            const colors = colorClasses[service.color];
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="group"
              >
                <div className={`h-full rounded-2xl bg-slate-900/50 border border-slate-800 overflow-hidden transition-all duration-500 hover:border-slate-700 hover:shadow-2xl ${colors.glow}`}>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className={`inline-flex p-3 rounded-xl ${colors.bg} ${colors.border} border mb-4`}>
                      <service.icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-center mt-12"
        >
          <Link
            to={createPageUrl('Services')}
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Explore all services <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
